import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk } from '@reduxjs/toolkit'

import { taxInformationModalConfirmedTaxInformationSelector } from '../../billing'
import {
  addonsSelector,
  countryCodeSelector,
  dealSelector,
  externalCustomerSelector,
  planSelector,
  postalCodeSelector,
  quantitySelector
} from '../selectors'

export const calculateTax = createAsyncThunk(
  'payment/calculateTax',
  async (zipCode, { getState, rejectWithValue }) => {
    const state = getState()
    const { tier, period_unit: periodUnit } = planSelector(state)
    const { uuid } = dealSelector(state)
    const addons = addonsSelector(state)
    const quantity = quantitySelector(state)
    const externalCustomerId = externalCustomerSelector(state)
    const confirmedTaxInfo = taxInformationModalConfirmedTaxInformationSelector(state)
    const countryCode = countryCodeSelector(state)
    const postalCode = postalCodeSelector(state)
    const currency = 'USD'

    const taxInformation = () => {
      if (!confirmedTaxInfo) return {}

      const {
        stateProvince: taxFormStateProvince,
        postalCode: taxFormPostalCode,
        countryCode: taxFormCountryCode,
        australiaTaxId: taxFormAustraliaTaxId,
        canadaFederalTaxId: taxFormCanadaFederalTaxId,
        canadaProvincialTaxId: taxFormCanadaProvincialTaxId,
        taxId: taxFormTaxId
      } = confirmedTaxInfo

      switch (taxFormCountryCode) {
        case 'AU':
          return {
            tax_id: taxFormAustraliaTaxId,
            country_code: taxFormCountryCode,
            postal_code: taxFormPostalCode,
            ...(taxFormStateProvince ? { state_code: taxFormStateProvince } : {})
          }
        case 'CA': {
          const result = {
            country_code: taxFormCountryCode,
            postal_code: taxFormPostalCode,
            ...(taxFormStateProvince ? { state_code: taxFormStateProvince } : {})
          }

          if (taxFormCanadaFederalTaxId) result.tax_id = taxFormCanadaFederalTaxId
          if (taxFormCanadaProvincialTaxId) result.secondary_tax_id = taxFormCanadaProvincialTaxId

          return result
        }
        default:
          return {
            tax_id: taxFormTaxId,
            country_code: taxFormCountryCode,
            postal_code: taxFormPostalCode,
            ...(taxFormStateProvince ? { state_code: taxFormStateProvince } : {})
          }
      }
    }

    const billingAddress = countryCode ? {
      postal_code: postalCode,
      country: countryCode
    } : {}

    const data = {
      plan: {
        id: tier,
        quantity,
      },
      addons,
      currency,
      external_customer_id: externalCustomerId,
      billing_period: periodUnit,
      billing_information: {
        billing_address: billingAddress,
        tax_information: taxInformation()
      }
    }

    let withoutTaxInfoCall = false
    let withoutTaxInfoSuccess = null
    let withoutTaxInfoResponse = null

    if (data?.billing_information?.tax_information && Object.keys(data.billing_information.tax_information).length !== 0) {
      // Tax information exists, make API call without tax data so we can compare
      const dataWithoutTaxInfo = {
        ...data,
        billing_information: {
          billing_address: {
            postal_code: taxInformation().postal_code,
            country: taxInformation().country_code
          },
          tax_information: {}
        }
      }

      withoutTaxInfoCall = true
      const withoutTaxInfo = await httpRequest({
        url: `/api/billing/deals/${uuid}/estimate`,
        method: 'POST',
        data: dataWithoutTaxInfo
      }
      )

      withoutTaxInfoSuccess = withoutTaxInfo[0]
      withoutTaxInfoResponse = withoutTaxInfo[1]
    }

    const [success, response] = await httpRequest({
      url: `/api/billing/deals/${uuid}/estimate`,
      method: 'POST',
      data
    })

    if (!success || (withoutTaxInfoCall && !withoutTaxInfoSuccess)) {
      return rejectWithValue(I18n.t('errors.billing.deal.sales_tax'))
    }

    return [response, withoutTaxInfoResponse]
  }
)
